import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { User, Lock, Shield, Briefcase, ArrowLeft } from "lucide-react";
import { adminLogin, adminForgotPassword, adminVerifyOTP, adminUpdatePassword } from "../ServiceAPI/AdminUser/api";
import "../styles/Auth.css";

const ROLE_CONFIG = {
  admin: {
    title: "Admin Portal",
    subtitle: "Secure access to RTO Management",
    description: "Manage operations, services, payments, and full system controls.",
    buttonText: "Access Admin Panel",
    emailPlaceholder: "Enter admin email",
    passwordPlaceholder: "Enter admin password",
    icon: Shield,
  },
  employee: {
    title: "Employee Portal",
    subtitle: "Secure access to employee workspace",
    description: "Handle day-to-day applications, user verification, and support tasks.",
    buttonText: "Access Employee Panel",
    emailPlaceholder: "Enter employee email",
    passwordPlaceholder: "Enter employee password",
    icon: Briefcase,
  },
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const { role: roleParam } = useParams();
  const [viewMode, setViewMode] = useState("login"); // "login", "forgot", "otp", "reset"
  const [form, setForm] = useState({ email: "", password: "" });
  const [forgotEmail, setForgotEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetAdminId, setResetAdminId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const selectedRole = useMemo(() => {
    const normalizedRole = String(roleParam || "").trim().toLowerCase();
    return ROLE_CONFIG[normalizedRole] ? normalizedRole : "";
  }, [roleParam]);
  const roleConfig = selectedRole ? ROLE_CONFIG[selectedRole] : null;

  useEffect(() => {
    if (selectedRole) {
      localStorage.setItem("adminSelectedRole", selectedRole);
    } else {
      localStorage.removeItem("adminSelectedRole");
    }
  }, [selectedRole]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await adminLogin({ ...form, role: selectedRole });

      if (response && response.token) {
        localStorage.setItem("adminAuth", "true");
        localStorage.setItem("adminToken", response.token);
        localStorage.setItem("adminSelectedRole", selectedRole);
        // The backend returns admin details in the 'admin' field
        const adminData = response.admin || response.user;
        if (adminData) {
          localStorage.setItem("adminUser", JSON.stringify(adminData));
        }
        navigate("/admin/dashboard");
      } else {
        if (response.success) {
          localStorage.setItem("adminAuth", "true");
          navigate("/admin/dashboard");
        } else {
          throw new Error("Invalid response from server");
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Login failed. Please check your credentials.");
    }
    setLoading(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await adminForgotPassword(forgotEmail);
      if (response.success) {
        setViewMode("otp");
        setSuccess(response.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await adminVerifyOTP(forgotEmail, otp);
      if (response.success) {
        setResetAdminId(response.adminId);
        setViewMode("reset");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await adminUpdatePassword(resetAdminId, newPassword);
      if (response.success) {
        setSuccess("Password updated successfully. Please login.");
        setViewMode("login");
        setForm({ ...form, email: forgotEmail });
        setForgotEmail("");
        setOtp("");
        setNewPassword("");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  if (!selectedRole) {
    return (
      <div className="auth-container admin-auth-stage">
        <div className="role-selection-shell">
          <div className="role-selection-header">
            <Shield size={46} className="admin-icon" />
            <h1>Choose Your Portal</h1>
            <p>Select how you want to sign in to the RTO management system.</p>
          </div>

          <div className="role-selection-grid">
            <button
              type="button"
              className="role-option-card"
              onClick={() => navigate("/admin/login/admin")}
            >
              <div className="role-option-icon role-option-admin">
                <Shield size={32} />
              </div>
              <h2>Admin</h2>
              <p>Full access to dashboard, services, payments, and configuration.</p>
              <span>Continue as Admin</span>
            </button>

            <button
              type="button"
              className="role-option-card"
              onClick={() => navigate("/admin/login/employee")}
            >
              <div className="role-option-icon role-option-employee">
                <Briefcase size={32} />
              </div>
              <h2>Employee</h2>
              <p>Access employee tools for application handling and user support.</p>
              <span>Continue as Employee</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const RoleIcon = roleConfig.icon;

  return (
    <div className="auth-container admin-auth-stage">
      <div className="admin-login-card">
        <div className="admin-login-header">
          <button
            type="button"
            className="role-back-btn"
            onClick={() => navigate("/admin/login")}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <RoleIcon size={48} className="admin-icon" />
          <h1>{roleConfig.title}</h1>
          <p>{roleConfig.description}</p>
        </div>


        <div className="auth-form-container">
          {viewMode === "login" && (
            <form className="auth-form admin-form" onSubmit={handleSubmit}>
              <h2>{roleConfig.subtitle}</h2>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message" style={{ color: 'green', marginBottom: '10px' }}>{success}</div>}

              <div className="input-group">
                <label htmlFor="email">Email</label>
                <div className="input-wrapper">
                  <User size={20} className="input-icon" />
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder={roleConfig.emailPlaceholder}
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <Lock size={20} className="input-icon" />
                  <input
                    id="password"
                    type="password"
                    name="password"
                    placeholder={roleConfig.passwordPlaceholder}
                    value={form.password}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div style={{ textAlign: 'right', marginBottom: '15px' }}>
                <button
                  type="button"
                  onClick={() => { setViewMode("forgot"); setError(""); setSuccess(""); }}
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', fontSize: '14px' }}
                >
                  Forgot Password?
                </button>
              </div>

              <button type="submit" disabled={loading} className="admin-login-btn">
                {loading ? "Authenticating..." : roleConfig.buttonText}
              </button>
            </form>
          )}

          {viewMode === "forgot" && (
            <form className="auth-form admin-form" onSubmit={handleForgotSubmit}>
              <h2>Reset {roleConfig.title.replace(" Portal", "")} Password</h2>
              <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>Enter your email to receive an OTP</p>
              {error && <div className="error-message">{error}</div>}

              <div className="input-group">
                <label htmlFor="forgotEmail">{roleConfig.title.replace(" Portal", "")} Email</label>
                <div className="input-wrapper">
                  <User size={20} className="input-icon" />
                  <input
                    id="forgotEmail"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    required
                  />
                </div>
              </div>

              <button type="submit" disabled={loading} className="admin-login-btn">
                {loading ? "Sending..." : "Send OTP"}
              </button>

              <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <button
                  type="button"
                  onClick={() => setViewMode("login")}
                  style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}

          {viewMode === "otp" && (
            <form className="auth-form admin-form" onSubmit={handleOTPSubmit}>
              <h2>Verify OTP</h2>
              <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>Enter the 6-digit code sent to {forgotEmail}</p>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message" style={{ color: 'green', marginBottom: '10px' }}>{success}</div>}

              <div className="input-group">
                <label htmlFor="otp">Enter OTP</label>
                <input
                  id="otp"
                  type="text"
                  maxLength="6"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  style={{ letterSpacing: '8px', textAlign: 'center', fontSize: '24px', fontWeight: 'bold' }}
                  placeholder="XXXXXX"
                  required
                />
              </div>

              <button type="submit" disabled={loading} className="admin-login-btn">
                {loading ? "Verifying..." : "Verify OTP"}
              </button>

              <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <button
                  type="button"
                  onClick={() => setViewMode("forgot")}
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Resend OTP
                </button>
              </div>
            </form>
          )}

          {viewMode === "reset" && (
            <form className="auth-form admin-form" onSubmit={handleResetSubmit}>
              <h2>Set New Password</h2>
              <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>Create a new secure password</p>
              {error && <div className="error-message">{error}</div>}

              <div className="input-group">
                <label htmlFor="newPass">New Password</label>
                <div className="input-wrapper">
                  <Lock size={20} className="input-icon" />
                  <input
                    id="newPass"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    minLength="6"
                    required
                  />
                </div>
              </div>

              <button type="submit" disabled={loading} className="admin-login-btn">
                {loading ? "Updating..." : "Update Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
